const GENERIC_MIME_TYPES = new Set(['application/octet-stream', 'binary/octet-stream'])

const AUDIO_EXTENSIONS = new Set([
    'mp3',
    'wav',
    'ogg',
    'oga',
    'opus',
    'm4a',
    'aac',
    'flac',
    'webm',
    'aif',
    'aiff',
    'aifc',
    'caf',
    'amr',
])

const AUDIO_MIME_BY_EXTENSION = {
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    oga: 'audio/ogg',
    opus: 'audio/ogg',
    m4a: 'audio/mp4',
    aac: 'audio/aac',
    flac: 'audio/flac',
    webm: 'audio/webm',
    aif: 'audio/aiff',
    aiff: 'audio/aiff',
    aifc: 'audio/aiff',
    caf: 'audio/x-caf',
    amr: 'audio/amr',
}

const MODEL_EXTENSIONS = new Set(['obj', 'glb', 'gltf', 'stl'])
const MODEL_MIME_TYPES = new Set([
    'model/gltf-binary',
    'model/gltf+json',
    'model/gltf-json',
    'model/gltf',
    'model/obj',
    'model/stl',
    'application/sla',
])

// HTML/ordinal documents: attachments whose content is a full web document (e.g. an inscribed
// text/html BSV ordinal file). Unminted ones render as compact download chips; once minted they
// render the inscription inline (tallFileEmbedKind 'htmlOrdinal'), so render and the height
// estimator stay in lockstep through that predicate.
const HTML_DOCUMENT_EXTENSIONS = new Set(['html', 'htm', 'xhtml'])
const HTML_DOCUMENT_MIME_TYPES = new Set(['text/html', 'application/xhtml+xml'])

export function normalizedFileEmbedMimeType(file = {}) {
    return (file.content_type || file.contentType || file.mime || file.type || '')
        .split(';')[0]
        ?.trim()
        .toLowerCase()
}

export function fileEmbedDisplayName(file = {}) {
    return file.name || file.filename || file.original_filename || 'File'
}

export function fileEmbedExtension(file = {}) {
    const fileNameCandidate =
        file.name || file.filename || file.original_filename || file.url || file.file_url || ''
    const value = String(fileNameCandidate)
    const withoutQuery = value.split(/[?#]/)[0]
    const lastSegment = withoutQuery.split('/').pop() || withoutQuery
    const fileName = (() => {
        try {
            return decodeURIComponent(lastSegment).toLowerCase()
        } catch (e) {
            return lastSegment.toLowerCase()
        }
    })()

    return fileName.includes('.') ? fileName.split('.').pop() : ''
}

export function classifyFileEmbedMedia(file = {}) {
    const rawMimeType = normalizedFileEmbedMimeType(file)
    const fileExtension = fileEmbedExtension(file)
    const isGenericMimeType = !rawMimeType || GENERIC_MIME_TYPES.has(rawMimeType)

    let isAudio = false
    let audioSourceType
    if (rawMimeType?.startsWith('audio/')) {
        isAudio = true
        audioSourceType = rawMimeType
    } else if (AUDIO_EXTENSIONS.has(fileExtension)) {
        isAudio = true
        audioSourceType =
            AUDIO_MIME_BY_EXTENSION[fileExtension] || `audio/${fileExtension || 'mpeg'}`
    }

    if (isAudio && !audioSourceType) {
        audioSourceType =
            AUDIO_MIME_BY_EXTENSION[fileExtension] ||
            (rawMimeType && !isGenericMimeType ? rawMimeType : `audio/${fileExtension || 'mpeg'}`)
    }

    let is3DModel = false
    if (!isAudio) {
        if (rawMimeType?.startsWith('model/')) {
            is3DModel = true
        } else if (MODEL_MIME_TYPES.has(rawMimeType)) {
            is3DModel = true
        } else if (isGenericMimeType && MODEL_EXTENSIONS.has(fileExtension)) {
            is3DModel = true
        }
    }

    let isHtmlDocument = false
    if (!isAudio && !is3DModel) {
        if (HTML_DOCUMENT_MIME_TYPES.has(rawMimeType)) {
            isHtmlDocument = true
        } else if (isGenericMimeType && HTML_DOCUMENT_EXTENSIONS.has(fileExtension)) {
            isHtmlDocument = true
        }
    }

    return {
        is3DModel,
        isAudio,
        isHtmlDocument,
        audioSourceType,
    }
}

/**
 * Attachment kinds that render a TALL inline embed instead of the compact download chip row:
 * - 'model': 3D model files render the fixed 400px `.modelViewer` (file-embed.module.scss).
 * - 'htmlOrdinal': a minted HTML file renders its inscription in the same 400px window,
 *   the way the ordinals stream and wallet show HTML ordinals.
 * Shared by the FileEmbed render dispatch and the static-geometry height estimators
 * (lib/answerPlaceholderHeight), which must mirror the render's type detection exactly.
 *
 * @param {any} file
 * @returns {'model' | 'htmlOrdinal' | null}
 */
export function tallFileEmbedKind(file = {}) {
    if (!file || typeof file !== 'object') return null
    const { is3DModel, isHtmlDocument } = classifyFileEmbedMedia(file)
    if (is3DModel) return 'model'
    if (isHtmlDocument && file.bsv_ordinal?.id) return 'htmlOrdinal'
    return null
}

export function genericFileDisplay(file = {}) {
    const displayName = fileEmbedDisplayName(file)
    const rawMimeType = normalizedFileEmbedMimeType(file)
    const lowerName = displayName.toLowerCase()
    const extension = lowerName.includes('.') ? lowerName.split('.').pop() : ''

    let typeLabel = 'File'
    if (rawMimeType === 'application/pdf' || extension === 'pdf') {
        typeLabel = 'PDF'
    } else if (extension) {
        typeLabel = extension.toUpperCase()
    } else if (rawMimeType) {
        typeLabel = rawMimeType.split('/').pop()?.toUpperCase() || 'File'
    }

    return {
        displayName,
        typeLabel,
    }
}
